import { useMemo, useState } from 'react'
import { type UseFormReturn, useWatch } from 'react-hook-form'

import { type MarketplacePartnerRegisterPageFormV3Values } from './marketplacePartnerRegisterPageFormV3RHFModel'
import { type MarketplacePartnerRegisterPageDictionaryOption } from './useMarketplacePartnerRegisterPageGetDictionaryOptionsByName'

type CategorySelectionEntry = {
  isChecked: boolean
  isSelected: boolean
  key: string
  label: string
}

const makeCategorySelectionEntries = ({
  checkedCategoryKeys,
  options,
  selectedCategoryKeys,
}: {
  checkedCategoryKeys: string[]
  options: MarketplacePartnerRegisterPageDictionaryOption[]
  selectedCategoryKeys: string[]
}): CategorySelectionEntry[] => {
  return options.map((category) => ({
    isChecked: checkedCategoryKeys.includes(category.key),
    isSelected: selectedCategoryKeys.includes(category.key),
    key: category.key,
    label: category.label,
  }))
}

type UseMarketplacePartnerRegisterPageFormV3CategoriesArgs = {
  categoriesOptions?: MarketplacePartnerRegisterPageDictionaryOption[]
  form: UseFormReturn<MarketplacePartnerRegisterPageFormV3Values>
}

export const useMarketplacePartnerRegisterPageFormV3Categories = ({
  categoriesOptions,
  form,
}: UseMarketplacePartnerRegisterPageFormV3CategoriesArgs): {
  categoriesSelectionEntries: CategorySelectionEntry[]
  handleCheck: (categoryKey: string, checked: boolean) => void
  moveCategories: (toSelected: boolean) => void
  toggleTargetCountry: (value: string, checked: boolean) => void
  watchedTargetCountries: string[]
} => {
  const watchedCategories = useWatch({
    control: form.control,
    name: 'categories',
  })
  const watchedTargetCountries = useWatch({
    control: form.control,
    name: 'targetCountries',
  })
  const [checkedCategoryKeys, setCheckedCategoryKeys] = useState<string[]>([])

  const categoriesSelectionEntries = useMemo(
    () =>
      makeCategorySelectionEntries({
        checkedCategoryKeys,
        options: categoriesOptions ?? [],
        selectedCategoryKeys: watchedCategories,
      }),
    [categoriesOptions, checkedCategoryKeys, watchedCategories],
  )

  const handleCheck = (categoryKey: string, checked: boolean): void => {
    setCheckedCategoryKeys((prev) =>
      checked
        ? [...prev, categoryKey]
        : prev.filter((key) => key !== categoryKey),
    )
  }

  const moveCategories = (toSelected: boolean): void => {
    const movedKeys = categoriesSelectionEntries
      .filter((entry) => entry.isChecked && entry.isSelected !== toSelected)
      .map((entry) => entry.key)

    const nextValues = toSelected
      ? [...new Set([...watchedCategories, ...movedKeys])]
      : watchedCategories.filter((value) => !movedKeys.includes(value))

    form.setValue('categories', nextValues, { shouldValidate: true })

    if (nextValues.length > 0) {
      form.clearErrors('categories')
    }

    setCheckedCategoryKeys((prev) =>
      prev.filter((key) => !movedKeys.includes(key)),
    )
  }

  const toggleTargetCountry = (value: string, checked: boolean): void => {
    const nextValues = checked
      ? [...watchedTargetCountries, value]
      : watchedTargetCountries.filter((currentValue) => currentValue !== value)

    form.setValue('targetCountries', nextValues, { shouldValidate: true })

    if (nextValues.length > 0) {
      form.clearErrors('targetCountries')
    }
  }

  return {
    categoriesSelectionEntries,
    handleCheck,
    moveCategories,
    toggleTargetCountry,
    watchedTargetCountries,
  }
}
